<template>
  <v-toolbar app class="app-toolbar" dark fixed flat>
    <v-toolbar-title class="toolbar-title">
      <v-toolbar-side-icon
        @click="handleDrawerToggle"
        class="drawer-toggle"
        aria-label="Abrir o cerrar menú principal"
      ></v-toolbar-side-icon>
    </v-toolbar-title>
    <v-spacer></v-spacer>
    <v-toolbar-items class="toolbar-items">
      <!-- Notificaciones -->
      <v-tooltip bottom v-if="canSeeNotifications">
        <template slot="activator">
          <v-btn
            @click="$router.push('/notificaciones')"
            flat
            icon
            round
            class="toolbar-btn toolbar-btn--notifications"
          >
            <v-badge
              v-if="notificationsTotal > 0"
              :content="notificationsTotal"
              color="#e5252a"
              overlap
            >
              <v-icon medium>notifications</v-icon>
            </v-badge>
            <v-icon v-else medium>notifications</v-icon>
          </v-btn>
        </template>
        <span>Notificaciones</span>
      </v-tooltip>

      <!-- Saludo + nombre (oculto en mobile) -->

      <!-- Distribuidor (solo rol DIST) -->
      <div
        v-if="sessionUser.ROLE === 'DIST' && sessionDistributor"
        class="toolbar-distributor"
      >
        <v-icon small class="toolbar-distributor__icon">store</v-icon>
        <span class="toolbar-distributor__name">{{
          sessionDistributor.name
        }}</span>
      </div>

      <!-- Avatar + dropdown -->
      <v-menu
        :nudge-bottom="10"
        offset-y
        origin="top right"
        transition="scale-transition"
        class="toolbar-menu"
      >
        <v-btn
          v-if="isLoggedIn"
          flat
          icon
          large
          round
          slot="activator"
          class="toolbar-btn toolbar-btn--user"
          :aria-label="'Cuenta de ' + roleLabel"
        >
          <v-avatar class="user-avatar" :color="userAvatarColor" size="38px">{{
            roleBadge
          }}</v-avatar>
        </v-btn>
        <div class="user-menu">
          <div class="user-menu__header">
            <v-avatar
              class="user-menu__avatar"
              :color="userAvatarColor"
              size="44px"
              >{{ roleBadge }}</v-avatar
            >
            <div class="user-menu__info">
              <p class="user-menu__name">{{ displayName }}</p>
              <p class="user-menu__role">{{ roleLabel }}</p>
            </div>
          </div>
          <v-divider></v-divider>
          <v-list class="pa-0 user-menu__list">
            <v-list-tile
              v-for="(item, index) in items"
              :key="index"
              :disabled="item.disabled"
              :href="item.href"
              :target="item.target"
              :to="!item.href ? { name: item.name } : null"
              @click="item.click"
              rel="noopener"
              ripple="ripple"
            >
              <v-list-tile-action v-if="item.icon">
                <v-icon>{{ item.icon }}</v-icon>
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title>{{ item.title }}</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
          </v-list>
        </div>
      </v-menu>
    </v-toolbar-items>
  </v-toolbar>
</template>
<script>
import { mapGetters } from 'vuex'
export default {
  name: 'AppToolbar',
  props: {
    notificationsTotal: Number,
  },
  data() {
    return {
      items: [
        {
          icon: 'logout',
          href: '#',
          title: 'Cerrar Sesión',
          click: this.handleLogut,
        },
      ],
    }
  },
  computed: {
    ...mapGetters('session', {
      isLoggedIn: 'isLoggedIn',
      sessionUser: 'sessionUser',
      sessionDistributor: 'sessionDistributor',
    }),
    userAvatarColor() {
      const roleColors = {
        GEM: '#e5252a',
        ADM: '#254f87',
        COL: '#c79a2b',
        DIST: '#254f87',
        LAB: '#c79a2b',
        PAG: '#e5252a',
        ENT: '#254f87',
      }
      return roleColors[this.sessionUser.ROLE] || '#254f87'
    },
    displayName() {
      const full = this.sessionUser.FULL_NAME || ''
      return full.split(' ')[0] || full || 'Usuario'
    },
    roleBadge() {
      return this.sessionUser.ROLE === 'MENSAJERO'
        ? 'MSJ'
        : this.sessionUser.ROLE
    },
    roleLabel() {
      const labels = {
        GEM: 'Gerente General',
        ADM: 'Administrador',
        COL: 'Colaborador',
        PAG: 'Pagos',
        ENT: 'Entregas',
        CLI: 'Cliente',
        LAB: 'Laboratorio',
        DIST: 'Distribuidor',
        TRA: 'Transportista',
        MENSAJERO: 'Mensajero',
      }
      return labels[this.sessionUser.ROLE] || this.sessionUser.ROLE
    },
    canSeeNotifications() {
      const role = this.sessionUser.ROLE
      return role === 'GEM' || role === 'ADM'
    },
  },
  methods: {
    handleDrawerToggle() {
      this.$emit('side-icon-click')
    },
    handleLogut() {
      this.$store.dispatch('session/logout').then(() => {
        location.href = '/auth/login'
      })
    },
  },
}
</script>

<style lang="stylus" scoped>
/* === Tokens de marca (deben coincidir con AppDrawer.vue) === */
$bg-primary    = #0f172a   /* slate-900: fondo base */
$bg-secondary  = #0a0f1c   /* slate más oscuro: acento gradient */
$border-soft   = rgba(148, 163, 184, 0.08)   /* slate-400 al 8% */
$text-primary  = rgba(226, 232, 240, 0.85)
$text-muted    = rgba(148, 163, 184, 0.85)
$brand-red     = #e5252a
$brand-gold    = #c79a2b
$brand-navy    = #254f87

.app-toolbar
  background: linear-gradient(180deg, $bg-primary 0%, $bg-secondary 100%) !important
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.25) !important
  border-bottom: 1px solid $border-soft
  position: relative

  /* Iconos outlined (mismo font que el drawer) */
  /deep/ .v-icon
    font-family: 'Material Icons Outlined', 'Material Icons' !important
    font-weight: 400
    font-feature-settings: 'liga'

  &::after
    content: ''
    position: absolute
    left: 0
    right: 0
    bottom: -1px
    height: 2px
    background: linear-gradient(90deg, $brand-red 0%, $brand-gold 50%, $brand-navy 100%)
    opacity: 0.75

.toolbar-title
  display: flex
  align-items: center
  gap: 10px

.drawer-toggle
  color: $text-primary !important
  transition: color .2s ease, background-color .2s ease
  border-radius: 8px

  &:hover
    color: $brand-gold !important
    background: rgba(255, 255, 255, 0.05)

.toolbar-btn
  color: $text-primary !important
  transition: all .18s ease
  border-radius: 8px

  .v-icon
    color: inherit !important
    transition: color .18s ease

  &:hover
    color: #ffffff !important
    background: rgba(255, 255, 255, 0.05)

  &--notifications
    margin-right: 8px

    &:hover
      color: $brand-red !important
      background: rgba(229, 37, 42, 0.10)

  &--user
    margin-left: 4px

.toolbar-items
  align-items: center
  gap: 4px

.toolbar-greeting
  display: flex
  flex-direction: column
  align-items: flex-end
  justify-content: center
  line-height: 1.2
  margin: 0 10px 0 6px
  padding-right: 4px

  &__hello
    font-size: 11px
    color: rgba(148, 163, 184, 0.85)
    font-weight: 500
    letter-spacing: 0.02em

  &__name
    font-size: 13.5px
    font-weight: 600
    color: rgba(255, 255, 255, 0.92)
    letter-spacing: 0.01em

.toolbar-distributor
  display: flex
  align-items: center
  gap: 7px
  background: rgba(199, 154, 43, 0.12)
  border: 1px solid rgba(199, 154, 43, 0.30)
  border-radius: 8px
  padding: 6px 14px
  margin: 0 8px 0 0

  &__icon
    color: $brand-gold !important
    font-size: 19px !important

  &__name
    font-size: 14px
    font-weight: 600
    color: $brand-gold
    letter-spacing: 0.01em
    white-space: nowrap
    max-width: 220px
    overflow: hidden
    text-overflow: ellipsis

@media (max-width: 768px)
  .toolbar-distributor
    max-width: 130px
    .toolbar-distributor__name
      max-width: 100px

@media (max-width: 600px)
  .toolbar-greeting
    display: none

  .toolbar-distributor
    display: none

.user-avatar
  font-weight: 700
  font-size: 13px
  color: #ffffff !important
  letter-spacing: 0.02em
  box-shadow: 0 0 0 2px $bg-primary, 0 0 0 3px $brand-gold
  transition: all .2s ease

  &:hover
    box-shadow: 0 0 0 2px $bg-primary, 0 0 0 3px $brand-red, 0 6px 16px rgba(229, 37, 42, 0.25)
    transform: scale(1.03)

.toolbar-menu ::v-deep .v-menu__content
  border-radius: 14px
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.18), 0 2px 8px rgba(15, 23, 42, 0.08)
  margin-top: 6px
  border: 1px solid #eef0f4
  overflow: hidden
  min-width: 240px

.user-menu
  background: #ffffff

.user-menu__header
  display: flex
  align-items: center
  gap: 12px
  padding: 14px 16px
  background: linear-gradient(135deg, rgba(37, 79, 135, 0.06), rgba(199, 154, 43, 0.06))

.user-menu__avatar
  font-weight: 700
  font-size: 15px
  color: #ffffff !important
  box-shadow: 0 0 0 2px #ffffff, 0 0 0 3px $brand-gold
  flex-shrink: 0

.user-menu__info
  flex: 1
  min-width: 0
  line-height: 1.25

.user-menu__name
  margin: 0
  font-size: 14px
  font-weight: 700
  color: #172133
  white-space: nowrap
  overflow: hidden
  text-overflow: ellipsis

.user-menu__role
  margin: 2px 0 0
  font-size: 11.5px
  color: $brand-navy
  font-weight: 600
  letter-spacing: 0.02em

.user-menu__list ::v-deep .v-list__tile
  border-radius: 0
  height: 44px
  color: #4b5563 !important
  transition: all .15s ease
  border-left: 3px solid transparent
  font-size: 13.5px

.user-menu__list ::v-deep .v-list__tile .v-list__tile__action .v-icon
  color: #94a3b8 !important
  transition: color .15s ease
  font-size: 20px

.user-menu__list ::v-deep .v-list__tile .v-list__tile__title
  font-size: 13.5px
  font-weight: 500

.user-menu__list ::v-deep .v-list__tile:hover
  background: rgba(229, 37, 42, 0.06) !important
  border-left-color: $brand-red
  color: $brand-red !important
  font-weight: 600

.user-menu__list ::v-deep .v-list__tile:hover .v-list__tile__action .v-icon
  color: $brand-red !important
</style>
